import React, { useState, useEffect } from 'react';
import EditableSection from './EditableSection';
import MonthYearPicker from './MonthYearPicker';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const str = String(dateStr);
  if (str.toLowerCase() === 'present') return 'Present';
  const [year, month] = str.split('-');
  if (!year || !month) return str;
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const renderDates = (start, end) => {
  if (!start && !end) return '';
  if (start && (!end || String(end).toLowerCase() === 'present')) return formatDate(start) + ' – Present';
  if (!start && end) return 'Until ' + formatDate(end);
  return `${formatDate(start)} – ${formatDate(end)}`;
};

const EducationEditor = ({ data, onSave, onRemove }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data || []);
  const [expandedIndex, setExpandedIndex] = useState(0);

  useEffect(() => {
    if (data) setEditData(data);
  }, [data]);

  const handleSave = () => {
    onSave(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data || []);
    setIsEditing(false);
  };

  const addItem = () => {
    setEditData([...editData, { degree: '', school: '', location: '', start_date: '', end_date: '' }]);
    setExpandedIndex(editData.length);
  };

  const updateItem = (index, field, value) => {
    const newData = [...editData];
    newData[index] = { ...newData[index], [field]: value };
    setEditData(newData);
  };

  const removeItem = (index) => {
    const newData = editData.filter((_, i) => i !== index);
    setEditData(newData);
    if (expandedIndex === index) {
      setExpandedIndex(Math.max(0, index - 1));
    } else if (expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const toggleExpand = (index) => {
    if (expandedIndex === index) setExpandedIndex(-1);
    else setExpandedIndex(index);
  };

  return (
    <EditableSection 
      title="Education"
      isEditing={isEditing} 
      onEdit={() => setIsEditing(true)} 
      onCancel={handleCancel} 
      onSave={handleSave}
      onRemove={onRemove}
    >
      {isEditing ? (
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200 font-sans">
          <div className="space-y-4 mb-4">
            {editData.map((edu, idx) => (
              <div key={idx} className="border border-gray-300 rounded bg-white overflow-hidden shadow-sm">
                <div 
                  className="bg-gray-100 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => toggleExpand(idx)}
                >
                  <div className="font-medium text-gray-800 flex-1 truncate">
                    {edu.degree && edu.school ? `${edu.degree} at ${edu.school}` : (edu.degree || edu.school || 'New Education')}
                  </div>
                  <div className="flex gap-2 items-center">
                    <button 
                      onClick={(e) => { e.stopPropagation(); removeItem(idx); }} 
                      className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                      title="Remove Education"
                    >
                      <Trash2 size={16} />
                    </button>
                    {expandedIndex === idx ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
                  </div>
                </div>
                
                {expandedIndex === idx && (
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Degree</label>
                      <input 
                        type="text" 
                        value={edu.degree || ''} 
                        onChange={(e) => updateItem(idx, 'degree', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="e.g. B.Tech in Computer Science"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                      <input 
                        type="text" 
                        value={edu.school || ''} 
                        onChange={(e) => updateItem(idx, 'school', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="e.g. University Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input 
                        type="text" 
                        value={edu.location || ''} 
                        onChange={(e) => updateItem(idx, 'location', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="e.g. City, Country"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <MonthYearPicker 
                        value={edu.start_date || ''} 
                        onChange={(val) => updateItem(idx, 'start_date', val)}
                      />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-sm font-medium text-gray-700">End Date</label>
                        <label className="text-xs flex items-center gap-1 text-gray-600 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={edu.end_date === 'Present'}
                            onChange={(e) => updateItem(idx, 'end_date', e.target.checked ? 'Present' : '')}
                          />
                          Present
                        </label>
                      </div>
                      <MonthYearPicker 
                        value={edu.end_date === 'Present' ? '' : (edu.end_date || '')} 
                        onChange={(val) => updateItem(idx, 'end_date', val)}
                        disabled={edu.end_date === 'Present'}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {editData.length === 0 && <p className="text-sm text-gray-500 italic py-2">No education added yet.</p>}
          </div>
          <button onClick={addItem} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium">
            <Plus size={16} /> Add Education
          </button>
        </div>
      ) : (
        <div className="resume-content text-[15px] space-y-4">
          {(!data || data.length === 0) ? (
            <p className="text-gray-400 italic">No education provided.</p>
          ) : (
            data.map((edu, idx) => (
              <div key={idx} className="mb-2">
                <div className="flex justify-between items-baseline mb-[2px]">
                  <div className="font-bold">
                    {edu.degree} <span className="font-normal">&mdash; {edu.school}</span>
                  </div>
                  <div className="text-[14.5px] whitespace-nowrap ml-4">
                    {edu.location}
                  </div>
                </div>
                <div className="italic text-[14.5px] text-gray-700">
                  {edu.dates ? edu.dates : renderDates(edu.start_date, edu.end_date)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </EditableSection>
  );
};

export default EducationEditor;
